import { create } from 'zustand';
import {
  removeAuthToken,
  setAuthToken,
  removeLocationHeader,
  setLocationHeader,
} from '../api/clients';
import { isApiError } from '../api/errors';
import { getAppConfig } from '../config/appConfigRef';
import { buildElvaLoginUrl, getLoginRedirectUri } from '../config/getElvaLoginUrl';
import { useLocationStore } from '../location/locationStore';
import { ascendIdForLocationUuid } from '../location/resolveLocationHeaderValue';
import { clearPkceSession, createOAuthState, createPkcePair, storePkceSession } from './pkce';
import { parseAccessTokenPayload, readAccessTokenExpiry } from './parseAccessToken';
import {
  logoutAllSessions,
  logoutSession,
  redeemHandoffCode,
  refreshSessionRequest,
  switchLocationRequest,
} from './sessionApi';
import { computeExpiresAt, resolveSessionStatus, sessionPayloadToState } from './sessionMapper';
import {
  getStoredRefreshToken,
  removeStoredRefreshToken,
  setStoredRefreshToken,
} from './refreshTokenStorage';
import { getStoredToken, removeStoredToken, setStoredToken } from './tokenStorage';
import { wireSessionToApi } from './wireSessionToApi';
import { clearAuthQueryCache } from './sessionCleanup';
import type {
  Access,
  AccessBlockReason,
  AuthStatus,
  Branch,
  Credit,
  LocationSummary,
  MePayload,
  Organization,
  PrincipalKind,
  SessionPayload,
  Subscription,
  User,
} from './types';

interface AuthState {
  organization: Organization | null;
  locations: LocationSummary[];
  user: User | null;
  access: Access | null;
  principalKind: PrincipalKind | null;
  branches: Branch[];
  subscriptions: Subscription[];
  credits: Credit[];
  accessToken: string | null;
  refreshToken: string | null;
  expiresAt: number | null;
  isLoading: boolean;
  status: AuthStatus;
  blockReason: AccessBlockReason | null;
  navigate: ((path: string) => void) | null;
  setNavigate: (navigate: (path: string) => void) => void;
  initialize: () => void;
  /** Boot / reload: establish session from storage (JWT decode → refresh → GET /me). */
  restoreSession: () => Promise<void>;
  /** @deprecated Use `restoreSession`. */
  fetchUser: () => Promise<void>;
  redeemCallback: (code: string) => Promise<void>;
  refreshSession: () => Promise<string | null>;
  switchActiveLocation: (locationId: string) => Promise<void>;
  logout: () => Promise<void>;
  logoutEverywhere: () => Promise<void>;
  redirectToLogin: () => void;
  startLoginRedirect: () => Promise<void>;
}

const emptySession = {
  organization: null,
  locations: [] as LocationSummary[],
  user: null,
  access: null,
  principalKind: null,
  branches: [] as Branch[],
  subscriptions: [] as Subscription[],
  credits: [] as Credit[],
  accessToken: null,
  refreshToken: null,
  expiresAt: null,
} as const;

/**
 * Refresh-token rotation is single use, so two concurrent refreshes kill each other.
 * Everything queues behind one in-flight promise (API_MICROAPPS.md §4).
 */
let refreshInflight: Promise<string | null> | null = null;
/** Bumped on logout so in-flight refresh/redeem cannot re-apply a cleared session. */
let sessionGeneration = 0;

function clearStoredSession(): void {
  removeAuthToken();
  removeStoredToken();
  removeStoredRefreshToken();
  removeLocationHeader();
}

function resetLocationSelection(): void {
  useLocationStore.getState().resetLocationSelection();
}

function clearClientSession(): void {
  sessionGeneration += 1;
  refreshInflight = null;
  clearStoredSession();
  resetLocationSelection();
  clearPkceSession();
  clearAuthQueryCache();
}

/** Codes consumed by a hand-off redeem — single use, so never run twice (e.g. Strict Mode). */
const redeemInflight = new Map<string, Promise<void>>();

export const useAuthStore = create<AuthState>((set, get) => {
  function syncLocationWithSession(
    access: Access | null,
    locations: LocationSummary[],
  ): void {
    useLocationStore.getState().setSelectedCoreLocationId(null);

    if (access?.location) {
      useLocationStore.getState().setSelectedLocationUuid(access.location);
      setLocationHeader(ascendIdForLocationUuid(access.location, locations, access));
      return;
    }

    removeLocationHeader();

    const storedUuid = useLocationStore.getState().getSelectedLocationUuid();
    const belongsToSession =
      storedUuid !== null && locations.some((location) => location.id === storedUuid);
    if (!belongsToSession) {
      useLocationStore.getState().setSelectedLocationUuid(null);
    }
  }

  function applyMe(payload: MePayload, tokens: { accessToken: string; refreshToken?: string; expiresIn?: number }): void {
    const generationAtApply = sessionGeneration;
    const state = sessionPayloadToState(payload);
    const { status, blockReason } = resolveSessionStatus(state, getAppConfig().clientId);

    if (generationAtApply !== sessionGeneration) {
      return;
    }

    setAuthToken(tokens.accessToken);
    setStoredToken(tokens.accessToken);
    if (tokens.refreshToken) {
      setStoredRefreshToken(tokens.refreshToken);
    }

    syncLocationWithSession(state.access, state.locations);

    set({
      ...state,
      accessToken: tokens.accessToken,
      refreshToken: tokens.refreshToken ?? getStoredRefreshToken(),
      expiresAt:
        tokens.expiresIn !== undefined
          ? computeExpiresAt(tokens.expiresIn)
          : readAccessTokenExpiry(tokens.accessToken),
      isLoading: false,
      status,
      blockReason,
    });
  }

  function applySession(payload: SessionPayload): void {
    applyMe(payload, {
      accessToken: payload.accessToken,
      ...(payload.refreshToken ? { refreshToken: payload.refreshToken } : {}),
      expiresIn: payload.expiresIn,
    });
  }

  async function doRefresh(): Promise<string | null> {
    const generationAtStart = sessionGeneration;
    const storedRefreshToken = getStoredRefreshToken();
    if (!storedRefreshToken) {
      throw new Error('No refresh token');
    }

    const payload = await refreshSessionRequest(storedRefreshToken);
    if (generationAtStart !== sessionGeneration) {
      throw new Error('Session cleared during refresh');
    }
    applySession(payload);
    return payload.accessToken;
  }

  return {
    ...emptySession,
    isLoading: true,
    status: 'loading',
    blockReason: null,
    navigate: null,

    setNavigate: (navigate) => {
      set({ navigate });
    },

    startLoginRedirect: async () => {
      clearClientSession();
      set({ ...emptySession, isLoading: true, status: 'loading', blockReason: null });

      const redirectUri = getLoginRedirectUri();
      const { verifier, challenge } = await createPkcePair();
      const state = createOAuthState();
      storePkceSession(verifier, state, redirectUri);
      window.location.href = buildElvaLoginUrl({ challenge, state });
    },

    redirectToLogin: () => {
      void get().startLoginRedirect();
    },

    initialize: () => {
      wireSessionToApi();
      const token = getStoredToken();
      const refreshToken = getStoredRefreshToken();

      if (token) {
        set({ accessToken: token, refreshToken, expiresAt: readAccessTokenExpiry(token) });
      }
    },

    refreshSession: () => {
      refreshInflight ??= doRefresh().finally(() => {
        refreshInflight = null;
      });
      return refreshInflight;
    },

    redeemCallback: async (code) => {
      const existing = redeemInflight.get(code);
      if (existing) {
        await existing;
        return;
      }

      const redeem = (async () => {
        const { readPkceVerifier } = await import('./pkce');
        const verifier = readPkceVerifier();

        if (!verifier) {
          clearPkceSession();
          throw new Error('Missing PKCE verifier');
        }

        try {
          applySession(await redeemHandoffCode(code, verifier));
        } finally {
          clearPkceSession();
        }
      })();

      redeemInflight.set(code, redeem);
      try {
        await redeem;
      } finally {
        redeemInflight.delete(code);
      }
    },

    switchActiveLocation: async (locationId) => {
      // access is per location, so the token is re-minted rather than the header swapped.
      applySession(await switchLocationRequest(locationId));
    },

    restoreSession: async () => {
      set({ isLoading: true, status: 'loading' });

      get().initialize();

      const storedRefreshToken = getStoredRefreshToken();

      if (!storedRefreshToken) {
        clearStoredSession();
        resetLocationSelection();
        set({ ...emptySession, isLoading: false, status: 'unauthenticated', blockReason: null });
        return;
      }

      async function scopeUnscopedToken(fromToken: MePayload): Promise<void> {
        if (!fromToken.access && 'access' in fromToken) {
          const target =
            useLocationStore.getState().getSelectedLocationUuid() ??
            fromToken.locations[0]?.id;
          if (target) {
            try {
              await get().switchActiveLocation(target);
            } catch {
              // locked state — user can pick another location
            }
          }
        }
      }

      async function refreshAndScope(): Promise<boolean> {
        await get().refreshSession();
        const accessToken = get().accessToken;
        if (!accessToken) return false;

        const fromToken = parseAccessTokenPayload(accessToken);
        if (fromToken) {
          await scopeUnscopedToken(fromToken);
        }
        return true;
      }

      try {
        await refreshAndScope();
        return;
      } catch (error) {
        if (isApiError(error, 'location_not_in_scope')) {
          useLocationStore.getState().setSelectedLocationUuid(null);
          removeLocationHeader();
          try {
            await refreshAndScope();
            return;
          } catch {
            // fall through
          }
        }
      }

      clearStoredSession();
      resetLocationSelection();
      set({ ...emptySession, isLoading: false, status: 'unauthenticated', blockReason: null });
    },

    fetchUser: async () => {
      await get().restoreSession();
    },

    logout: async () => {
      const refreshToken = getStoredRefreshToken() ?? get().refreshToken;

      try {
        if (refreshToken) {
          await logoutSession(refreshToken);
        }
      } catch {
        // local session is cleared either way
      } finally {
        clearClientSession();
        set({ ...emptySession, isLoading: false, status: 'unauthenticated', blockReason: null });
      }

      await get().startLoginRedirect().catch(() => undefined);
    },

    logoutEverywhere: async () => {
      const hadAccessToken = get().accessToken !== null;

      try {
        if (hadAccessToken) {
          await logoutAllSessions();
        }
      } catch {
        // local session is cleared either way
      } finally {
        clearClientSession();
        set({ ...emptySession, isLoading: false, status: 'unauthenticated', blockReason: null });
      }

      await get().startLoginRedirect().catch(() => undefined);
    },
  };
});
